/**
 * Las piezas de la libreta: el marcador de cada lado, el teclado propio y el
 * cuaderno donde se corrige.
 *
 * Viven aparte de la pantalla de anotar porque hay dos libretas con la misma
 * cara. La del torneo escribe en el servidor y la personal en el teléfono, pero
 * en la mesa se usan igual: mismo teclado, mismos botones grandes, misma hoja
 * rayada. Que el jugador no tenga que aprender dos es lo que importa, así que
 * estas piezas no saben de dónde sale la partida: reciben el marcador y avisan
 * qué se tocó.
 */

import { Fragment, useState } from 'react'
import type { ReactNode } from 'react'
import { quienSale, salidaInicial } from '@domino/core'
import { Icono } from './Icono.js'

/** Una mano anotada, con cómo quedó la partida después de ella. */
export interface ManoAnotada {
  numero: number
  bandoGanador: 1 | 2
  puntos: number
  bando1: number
  bando2: number
}

/**
 * Puntos que el organizador le restó a un bando en plena partida, con cómo
 * quedó la partida después. Solo en la libreta del torneo: la personal no tiene
 * árbitro.
 */
export interface RestaAnotada {
  despuesDeMano: number
  bando: 1 | 2
  puntos: number
  /** A quién le sacaron la tarjeta, y por qué. */
  nombre: string
  motivo: string
  bando1: number
  bando2: number
}

/** Lo que las piezas necesitan de una partida, venga de donde venga. */
export interface MarcadorDeLibreta {
  bando1: number
  bando2: number
  ganador: 1 | 2 | null
  objetivoPuntos: number
  manos: readonly ManoAnotada[]
}

/** Los valores que más salen en una mano, a un toque. */
const RAPIDOS = [10, 15, 20, 25, 30, 40] as const

/** Tres cifras alcanzan: ninguna mano pasa de 999. */
const CIFRAS = 3

const TECLAS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'limpiar', '0', 'borrar'] as const

export function puntosDe(b: 1 | 2, m: { bando1: number; bando2: number }): number {
  return b === 1 ? m.bando1 : m.bando2
}

/**
 * La primera fila de la libreta: volver a la izquierda, chico, y a la derecha
 * de qué partida se trata. En una sola fila y no en dos, para que la libreta
 * entera entre en la pantalla del teléfono sin desplazarse.
 */
export function CabezaLibreta({
  textoVolver,
  alVolver,
  children,
}: {
  textoVolver: string
  alVolver: () => void
  children: ReactNode
}) {
  return (
    <div className="libreta-cabeza">
      <button className="libreta-volver chica" onClick={alVolver}>
        <Icono nombre="atras" tamano={16} />
        {textoVolver}
      </button>
      <p className="libreta-mesa">{children}</p>
    </div>
  )
}

export function Lado({
  partida,
  bando,
  nombre,
  derecha = false,
}: {
  partida: MarcadorDeLibreta
  bando: 1 | 2
  nombre: string
  derecha?: boolean
}) {
  const puntos = puntosDe(bando, partida)
  const gano = partida.ganador === bando
  return (
    <div className={`libreta-lado ${derecha ? 'derecha' : ''} ${gano ? 'gana' : ''}`}>
      <span className="libreta-nombre">{nombre}</span>
      <span className="libreta-puntos">{puntos}</span>
      <span className="libreta-faltan">
        {gano ? 'ganó' : `faltan ${Math.max(0, partida.objetivoPuntos - puntos)}`}
      </span>
    </div>
  )
}

/** Una de las cuatro sillas, en el orden en que se juega (ver `sentarEnOrden`). */
export interface SillaDeLibreta {
  silla: number
  bando: 1 | 2
  nombre: string
}

/**
 * Quién sale en la mano que se va a jugar.
 *
 * Se anota una sola vez, y desde ahí la libreta lo sigue sola: la salida rota
 * hacia la derecha mano a mano (ver `quienSale`). Se pregunta por la mano que
 * viene y no por la primera porque es lo que la mesa tiene a la vista: el que
 * se acuerda de anotarla en la cuarta mano no tiene por qué acordarse de quién
 * salió en la primera. Lo que se guarda sí es la de la primera, deducida.
 *
 * Una sola fila, como el resto de la libreta: tiene que entrar en la pantalla
 * del teléfono sin desplazarse.
 */
export function Salida({
  sillas,
  salida,
  numero,
  orden,
  puede,
  enviando = false,
  alAnotar,
}: {
  sillas: readonly SillaDeLibreta[]
  /** La silla que salió en la primera mano, o nulo si no se anotó. */
  salida: number | null
  /** La mano que se va a jugar. */
  numero: number
  orden: [1 | 2, 1 | 2]
  /** Si se puede anotar o cambiar. Sin permiso solo se muestra. */
  puede: boolean
  enviando?: boolean
  /** La silla que salió en la primera mano, ya deducida; nulo la borra. */
  alAnotar: (salida: number | null) => void
}) {
  const [eligiendo, setEligiendo] = useState(false)
  if (sillas.length !== 4) return null

  const sale = quienSale(salida, numero)
  const quien = sillas.find((s) => s.silla === sale) ?? null
  const lado = (b: 1 | 2) => (b === orden[0] ? 'nosotros' : 'ellos')

  if (quien && !eligiendo) {
    return (
      <div className="libreta-salida">
        <span>
          <i className={lado(quien.bando)} />
          Sale en la mano {numero}: <b>{pila(quien.nombre)}</b>
        </span>
        {puede && (
          <button className="libreta-salida-cambiar" onClick={() => setEligiendo(true)} disabled={enviando}>
            Cambiar
          </button>
        )}
      </div>
    )
  }
  if (!puede) return null

  return (
    <div className="libreta-salida eligiendo" role="group" aria-label={`Quién sale en la mano ${numero}`}>
      <span>
        ¿Quién sale en la mano {numero}?
        {eligiendo && (
          <button className="libreta-salida-cambiar" onClick={() => setEligiendo(false)} disabled={enviando}>
            Cancelar
          </button>
        )}
      </span>
      <div className="libreta-salida-sillas">
        {sillas.map((s) => (
          <button
            key={s.silla}
            className={`${lado(s.bando)} ${s.silla === sale ? 'elegido' : ''}`}
            disabled={enviando}
            onClick={() => {
              setEligiendo(false)
              alAnotar(salidaInicial(s.silla, numero))
            }}
          >
            {pila(s.nombre)}
          </button>
        ))}
      </div>
    </div>
  )
}

function pila(nombre: string): string {
  return nombre.trim().split(' ')[0] ?? nombre
}

/**
 * El teclado propio: la cifra que se está escribiendo, los valores rápidos, los
 * números y los dos bandos. Anotar y corregir son lo mismo con otro título.
 */
export function Teclado({
  titulo,
  cifras,
  setCifras,
  listo,
  enviando,
  orden,
  nombre,
  elegido,
  resultado,
  alElegir,
}: {
  titulo: string
  cifras: string
  setCifras: (actualizar: (c: string) => string) => void
  listo: boolean
  enviando: boolean
  orden: [1 | 2, 1 | 2]
  nombre: Record<1 | 2, string>
  /** Al corregir, el bando que tenía la mano. */
  elegido?: 1 | 2
  /** Cómo queda el marcador de un bando si la mano es suya. */
  resultado: (b: 1 | 2) => string
  alElegir: (b: 1 | 2) => void
}) {
  function tocar(tecla: (typeof TECLAS)[number]) {
    if (tecla === 'limpiar') setCifras(() => '')
    else if (tecla === 'borrar') setCifras((c) => c.slice(0, -1))
    else setCifras((c) => (c.length < CIFRAS ? (c + tecla).replace(/^0+/, '') : c))
  }

  return (
    <>
      <div className={`libreta-pantallita ${elegido ? 'corrigiendo' : ''}`}>
        <span>{titulo}</span>
        <b>{cifras || '0'}</b>
      </div>

      <div className="libreta-rapidos" role="group" aria-label="Valores rápidos">
        {RAPIDOS.map((r) => (
          <button key={r} onClick={() => setCifras(() => String(r))} disabled={enviando}>
            {r}
          </button>
        ))}
      </div>

      <div className="libreta-teclado" role="group" aria-label="Teclado de puntos">
        {TECLAS.map((t) => (
          <button
            key={t}
            className={t === 'limpiar' || t === 'borrar' ? 'accion' : ''}
            onClick={() => tocar(t)}
            disabled={enviando}
            aria-label={t === 'borrar' ? 'Borrar la última cifra' : undefined}
          >
            {t === 'limpiar' ? 'Limpiar' : t === 'borrar' ? '⌫' : t}
          </button>
        ))}
      </div>

      <div className="libreta-bandos" role="group" aria-label="Para quién son los puntos">
        {orden.map((b, i) => (
          <button
            key={b}
            className={`libreta-bando ${i === 0 ? 'nosotros' : 'ellos'} ${elegido === b ? 'elegido' : ''}`}
            disabled={!listo || enviando}
            onClick={() => alElegir(b)}
          >
            {elegido ? 'Guardar para' : 'Para'} {nombre[b]}
            <small>{listo ? resultado(b) : 'escribí los puntos'}</small>
          </button>
        ))}
      </div>
    </>
  )
}

/**
 * El cartel de la corrección: que nadie confunda corregir con anotar.
 *
 * Va arriba del cuaderno mientras se corrige, y dice qué mano se está tocando
 * y qué tenía antes, para que el que mira el teléfono por encima del hombro
 * sepa que lo que se escribe reemplaza esa mano y no agrega otra.
 */
export function AvisoCorreccion({
  mano,
  quienes,
  alSoltar,
}: {
  /** La mano tocada, o nula mientras no se eligió ninguna. */
  mano: ManoAnotada | null
  quienes: Record<1 | 2, string>
  /** Suelta la mano sin cambiarla, para elegir otra. */
  alSoltar: () => void
}) {
  return (
    <div className="libreta-corrigiendo" role="status">
      <span>
        <b>{mano ? `Corrigiendo la mano ${mano.numero}` : 'Modo corrección'}</b>
        <small>
          {mano
            ? `Tenía ${mano.puntos} para ${quienes[mano.bandoGanador]}. Lo que elijas reemplaza esta mano.`
            : 'Tocá la mano que querés corregir.'}
        </small>
      </span>
      {mano && (
        <button className="libreta-salida-cambiar" onClick={alSoltar}>
          Soltar
        </button>
      )}
    </div>
  )
}

/**
 * El historial como la hoja de papel: una columna por bando, el número de mano
 * en el margen y, en cada renglón, los puntos de la mano y el acumulado. Cada
 * renglón se toca para corregirlo; sin `alElegir` es solo para mirar.
 */
export function Cuaderno<M extends ManoAnotada>({
  partida,
  orden,
  quienes,
  elegida,
  alElegir,
}: {
  partida: {
    bando1: number
    bando2: number
    manos: readonly M[]
    restas?: readonly RestaAnotada[]
  }
  orden: [1 | 2, 1 | 2]
  quienes: Record<1 | 2, string>
  elegida: number | null
  alElegir?: (m: M) => void
}) {
  /* Cada resta va en su renglón, después de la mano tras la que se anotó. La
     que apunta más allá de la última (se borró la mano) va al final, que es
     donde la cuenta la pone. No se toca: la corrige el organizador. */
  const restas = partida.restas ?? []
  const ultima = partida.manos.at(-1)?.numero ?? 0
  const restasTras = (n: number) =>
    restas.filter((r) => r.despuesDeMano === n || (n === ultima && r.despuesDeMano > ultima))
  const filaResta = (r: RestaAnotada, i: number) => (
    <div
      key={`resta-${r.despuesDeMano}-${i}`}
      className="cuaderno-fila cuaderno-resta"
      aria-label={`Sanción a ${r.nombre}: ${quienes[r.bando]} pierde ${r.puntos} puntos`}
    >
      <span aria-hidden="true">−</span>
      {orden.map((b) =>
        r.bando === b ? (
          <span key={b} className="cuaderno-celda">
            <b>−{r.puntos}</b>
            <small>{puntosDe(b, r)}</small>
          </span>
        ) : (
          <span key={b} className="cuaderno-celda nada">
            <span aria-hidden="true">·</span>
            <small>{puntosDe(b, r)}</small>
          </span>
        ),
      )}
      <small className="cuaderno-motivo">
        {r.nombre}: {r.motivo}
      </small>
    </div>
  )

  const celda = (m: M, b: 1 | 2) =>
    m.bandoGanador === b ? (
      <span className="cuaderno-celda">
        <b>{m.puntos}</b>
        <small>{puntosDe(b, m)}</small>
      </span>
    ) : (
      <span className="cuaderno-celda nada">
        <span aria-hidden="true">·</span>
        <small>{puntosDe(b, m)}</small>
      </span>
    )

  return (
    <div className="cuaderno">
      <div className="cuaderno-fila cuaderno-cabeza">
        <span />
        <span className="nosotros">{quienes[orden[0]]}</span>
        <span className="ellos">{quienes[orden[1]]}</span>
      </div>
      {restasTras(0).map(filaResta)}
      {partida.manos.map((m) => (
        <Fragment key={m.numero}>
          {alElegir ? (
            <button
              className={`cuaderno-fila ${elegida === m.numero ? 'elegida' : ''}`}
              onClick={() => alElegir(m)}
              aria-label={`Corregir la mano ${m.numero}: ${m.puntos} para ${quienes[m.bandoGanador]}`}
            >
              <span>M{m.numero}</span>
              {celda(m, orden[0])}
              {celda(m, orden[1])}
            </button>
          ) : (
            <div className="cuaderno-fila">
              <span>M{m.numero}</span>
              {celda(m, orden[0])}
              {celda(m, orden[1])}
            </div>
          )}
          {restasTras(m.numero).map(filaResta)}
        </Fragment>
      ))}
      <div className="cuaderno-fila cuaderno-total">
        <span>Σ</span>
        <span className="cuaderno-celda">
          <b>{puntosDe(orden[0], partida)}</b>
        </span>
        <span className="cuaderno-celda">
          <b>{puntosDe(orden[1], partida)}</b>
        </span>
      </div>
    </div>
  )
}
